// 🔒 PROTECTED — PricePAL Compare
// Owner: Garry Yee | Do not modify without explicit written permission
// See /app/PROTECTED_CODE_REGISTRY.md for full rules
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

/**
 * Trial Status Banner
 * Shows countdown and upgrade prompts during trial period
 * Displays on user dashboard
 */
const TrialStatusBanner = ({ userId, onUpgrade }) => {
  const navigate = useNavigate();
  const [trialStatus, setTrialStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    if (userId) {
      fetchTrialStatus();
    }
  }, [userId]);

  const fetchTrialStatus = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/users/${userId}/trial-status`);
      setTrialStatus(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching trial status:', error);
      setLoading(false);
    }
  };

  if (loading || !trialStatus) {
    return null;
  }

  // Don't show banner for paid users
  if (['INDIVIDUAL', 'FAMILY', 'ALPHA_TESTER', 'VIP_FOUNDER', 'EARLY_FOUNDER'].includes(trialStatus.subscription_tier)) {
    return null;
  }

  // TRIAL ACTIVE - Show countdown
  if (trialStatus.on_trial) {
    const daysRemaining = trialStatus.days_remaining;
    const isUrgent = daysRemaining <= 3; // Last 3 days of 7-day trial

    return (
      <div className={`rounded-xl p-6 mb-6 shadow-lg ${
        isUrgent 
          ? 'bg-gradient-to-r from-orange-500 to-red-500' 
          : 'bg-gradient-to-r from-blue-500 to-green-500'
      } text-white`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="text-5xl">
              {isUrgent ? '⚠️' : '🎉'}
            </div>
            <div>
              <h3 className="text-2xl font-bold">
                {isUrgent ? 'Trial Ending Soon!' : '7-Day Free Premium Trial Active'}
              </h3>
              <p className="text-lg mt-1">
                <span className="font-bold text-3xl">{daysRemaining}</span> {daysRemaining === 1 ? 'day' : 'days'} remaining
              </p>
              <p className="text-sm mt-1 opacity-90">
                {isUrgent 
                  ? 'Your card will be charged £1.99/month at the end of your trial. Cancel any time from Settings to avoid the charge.' 
                  : 'Enjoying unlimited scans, alerts, Kitchen AI and Full Fuel Finder.'}
              </p>
            </div>
          </div>
          
          <div className="flex flex-col space-y-2">
            <button
              onClick={() => onUpgrade ? onUpgrade() : navigate('/pricing')}
              className="bg-white text-blue-600 font-bold px-6 py-3 rounded-xl hover:bg-gray-100 transition-all shadow-lg"
            >
              View Plans
            </button>
            {isUrgent && (
              <button
                onClick={() => onUpgrade ? onUpgrade() : setShowUpgradeModal(true)}
                className="bg-yellow-400 text-gray-900 font-bold px-6 py-2 rounded-xl hover:bg-yellow-300 transition-all"
              >
                Upgrade Now
              </button>
            )}
          </div>
        </div>

        {/* Progress Bar - 7-day trial */}
        <div className="mt-4 bg-white bg-opacity-30 rounded-full h-2">
          <div 
            className="bg-white h-2 rounded-full transition-all duration-500"
            style={{ width: `${(daysRemaining / 7) * 100}%` }}
          />
        </div>
      </div>
    );
  }

  // TRIAL EXPIRED - Show upgrade prompt
  if (trialStatus.trial_expired && trialStatus.subscription_tier === 'FREE') {
    return (
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl p-6 mb-6 shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="text-5xl">💎</div>
            <div>
              <h3 className="text-2xl font-bold">Your Trial Has Ended</h3>
              <p className="text-lg mt-1">
                Missing unlimited scans and premium features?
              </p>
              <p className="text-sm mt-1 opacity-90">
                Upgrade to Premium (£1.99/mo) or Family (£2.99/mo) to continue saving!
              </p>
            </div>
          </div>
          
          <button
            onClick={() => onUpgrade ? onUpgrade() : navigate('/pricing')}
            className="bg-white text-purple-600 font-bold px-8 py-4 rounded-xl hover:bg-gray-100 transition-all shadow-lg text-lg"
          >
            Upgrade Now
          </button>
        </div>

        {/* Feature Comparison */}
        <div className="mt-4 bg-white bg-opacity-10 rounded-lg p-4">
          <p className="text-sm font-semibold mb-2">You're now on FREE tier with:</p>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>❌ Only 5 scans/day + 3 alerts + 2 receipts/mo</div>
            <div>❌ Only 5 price alerts</div>
            <div>❌ No bulk comparison</div>
            <div>❌ No cart optimizer</div>
          </div>
          <p className="text-sm font-semibold mt-3">Upgrade to get back:</p>
          <div className="grid grid-cols-2 gap-2 text-sm text-green-200">
            <div>✅ Unlimited scans</div>
            <div>✅ Unlimited alerts</div>
            <div>✅ Bulk comparison</div>
            <div>✅ Cart optimizer</div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default TrialStatusBanner;
